import { ArrowRight, TriangleAlert } from "lucide-react";
import { useEffect, useState } from "react";
import { ErrorState } from "@/components/shared/error-state";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { EntityCombobox } from "@/components/ui/entity-combobox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { useChangeReasons } from "@/hooks/use-change-reasons";
import {
  type PersonChangePreview,
  useDeactivatePerson,
  usePerson,
  usePersonChangePreview,
  usePersonDirectReports,
  usePersonPendingChanges,
  useUpdatePerson,
} from "@/hooks/use-persons";
import { formatDateOnly, todayLocal } from "@/lib/dates";
import type { PersonUpdateInput } from "@/schemas/persons";

/**
 * Fields whose change must be explained: org placement + title. When this
 * save touches any of them the dialog shows a required "Reason for change"
 * select (the admin-configured list at Settings → Config → Change reasons);
 * a save touching only other fields never shows it.
 */
const REASON_FIELDS = new Set([
  "title",
  "roleClassification",
  "departmentId",
  "roleTeamId",
  "managerId",
]);

/**
 * Human-readable calendar date from a stored effective date (UTC-midnight ISO).
 * Formats the date PART only — running the full instant through
 * `toLocaleDateString` would render a day early for viewers west of UTC.
 */
interface EffectiveDateConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  personId: string;
  /** The would-be update payload, diffed server-side for the preview. */
  updates: PersonUpdateInput | null;
  /** Called after the change is successfully scheduled (lets the parent close). */
  onScheduled?: () => void;
}

/**
 * Confirmation step for a person edit. Shows every field that changed
 * (resolved before→after). By default the change applies immediately (no date
 * is sent — the server applies it right away); ticking "Select a date for this
 * change" reveals a date input so the change can be scheduled instead. A
 * future date defers to the background worker; today/a past date still
 * applies immediately.
 */
export function EffectiveDateConfirmDialog({
  open,
  onOpenChange,
  personId,
  updates,
  onScheduled,
}: EffectiveDateConfirmDialogProps) {
  // Unchecked (default) = apply immediately, no date field shown. Checked =
  // the user picks the effective date (required before saving).
  const [scheduleForDate, setScheduleForDate] = useState(false);
  const [effectiveDate, setEffectiveDate] = useState("");
  // Overwrite-vs-keep choice when this save overlaps a change scheduled for a
  // different date. `null` = the user hasn't picked; a default is derived from
  // the dates (see `defaultOverwrite` below).
  const [overwriteChoice, setOverwriteChoice] = useState<"overwrite" | "keep" | null>(null);
  // Why the change was made — required when this save touches an org-placement
  // field (see REASON_FIELDS); hidden and unsent otherwise.
  const [changeReasonId, setChangeReasonId] = useState<string | null>(null);
  const preview = usePersonChangePreview(personId, updates, open);
  const pending = usePersonPendingChanges(personId);
  const update = useUpdatePerson(personId);
  const deactivate = useDeactivatePerson(personId);

  const changes = preview.data ?? [];
  const hasChanges = changes.length > 0;

  const needsReason = changes.some((c) => REASON_FIELDS.has(c.field));
  const changeReasons = useChangeReasons(needsReason);
  // Required field, empty list: without this the admin gets a form that can
  // never be submitted and no explanation.
  const reasonsUnavailable =
    needsReason && !changeReasons.isLoading && (changeReasons.isError || !changeReasons.data);
  const missingReason = needsReason && changeReasonId === null;

  // Deactivating a manager strands their direct reports (inactive people are
  // excluded from the org chart and the manager picker), so the save is gated
  // on choosing a new manager for every report — individually, or all at once
  // via the quick-set row. Key present in the map = a choice was made (null is
  // an explicit "no manager").
  const isDeactivating =
    updates?.status === "inactive" && changes.some((c) => c.field === "status");
  const directReports = usePersonDirectReports(personId);
  /**
   * The whole gate below is derived from `reports`, so an UNSETTLED read is not
   * neutral — a failed fetch leaves `data` undefined with `isLoading` false,
   * which reads as "nobody reports to this person" and lets the save through
   * with the reassignment step skipped entirely. Their reports are then left
   * pointing at an inactive manager, which the org chart and the manager picker
   * both exclude — the exact stranding this section exists to prevent, and it
   * shows no error at all. Same shape as `settled` in PersonRolesDialog.
   */
  const reportsSettled =
    !directReports.isLoading && !directReports.isError && directReports.data !== undefined;
  const reports = directReports.data ?? [];
  const [reassignments, setReassignments] = useState<Record<string, string | null>>({});
  // Reset per open: back to the immediate default every time the dialog opens
  // (it isn't remounted between opens — a future-dated save doesn't touch the
  // person row, so the parent's remount key is unchanged), and clear any
  // reassignment picks on close. Without the reset the dialog would reopen
  // with a previously ticked box + chosen date.
  useEffect(() => {
    if (open) {
      setScheduleForDate(false);
      setEffectiveDate("");
      setOverwriteChoice(null);
      setChangeReasonId(null);
    } else {
      setReassignments({});
    }
  }, [open]);
  const needsReassignment = isDeactivating && reports.length > 0;
  // Only blocks a DEACTIVATION: every other save is indifferent to who reports
  // to this person, and failing those too would be a gate nobody can clear.
  const reportsUnavailable = isDeactivating && !reportsSettled;
  // Deactivating also vacates any team this person leads (the server clears it
  // in the same transaction). Surfaced here so the admin is told rather than
  // discovering an empty lead slot on the org page later.
  const ledTeamName = usePerson(personId).data?.ledRoleTeamName ?? null;
  const vacatesLead = isDeactivating && ledTeamName !== null;
  const reassignmentIncomplete = needsReassignment && reports.some((r) => !(r.id in reassignments));

  // Unchecked box = immediate. Checked with today or a past date also applies
  // immediately (no pending row, no "scheduled change" icon); only a future
  // date is deferred to the background worker. ISO date-only strings compare
  // lexicographically.
  const isImmediate = !scheduleForDate || (effectiveDate !== "" && effectiveDate <= todayLocal());
  const missingDate = scheduleForDate && effectiveDate === "";

  // Scheduled changes on fields this save touches. Stored effective dates are
  // UTC-midnight ISO strings, so the date slice matches the picker's value.
  const changedFields = new Set(changes.map((c) => c.field));
  const today = todayLocal();
  /**
   * An UNSETTLED read is not neutral here either — same shape as
   * `reportsSettled` above, and the same consequence in reverse.
   *
   * `?? []` on a failed fetch means no overlap is found, so the
   * overwrite-or-keep choice never renders and defaults to "keep": the admin's
   * edit silently stacks behind an existing later-dated change that will
   * overwrite the value they just set, with no error shown. That is precisely
   * the silent overwrite this dialog was built to prevent, so the save is
   * blocked until the read settles.
   */
  const pendingSettled = !pending.isLoading && !pending.isError && pending.data !== undefined;
  const overlapping = (pending.data ?? []).filter((p) => changedFields.has(p.field));
  // Rows the server replaces automatically — already-due rows on an immediate
  // save, and same-date rows on a dated save. Surfaced as informational only.
  const autoReplaced = overlapping.filter((p) => {
    const pDate = p.effectiveDate.slice(0, 10);
    return isImmediate ? pDate <= today : pDate === effectiveDate;
  });
  // Rows scheduled for a DIFFERENT future date. These survive this save unless
  // the user chooses "overwrite" below — a surviving later-dated change would
  // silently replace this save's value when its date arrives.
  const overlapChoices = overlapping.filter((p) => !autoReplaced.includes(p));
  // Default per the agreed rules: a change landing BEFORE an existing scheduled
  // one (or immediately) overwrites it; a change landing after it stacks (both
  // apply, each on its own date).
  const newDate = isImmediate ? today : effectiveDate;
  const defaultOverwrite = overlapChoices.some((p) => p.effectiveDate.slice(0, 10) > newDate);
  const overwrite = overwriteChoice ?? (defaultOverwrite ? "overwrite" : "keep");

  async function handleSave() {
    if (
      !updates ||
      missingDate ||
      !hasChanges ||
      reassignmentIncomplete ||
      reportsUnavailable ||
      missingReason
    )
      return;
    // Only a dated save sends `effectiveDate`; omitting it applies immediately.
    const when = scheduleForDate ? { effectiveDate } : {};
    // "Overwrite" withdraws the overlapping scheduled changes in the same save.
    const cancels =
      overlapChoices.length > 0 && overwrite === "overwrite"
        ? { cancelPendingChangeIds: overlapChoices.map((p) => p.id) }
        : {};
    // The reason rides along only when the dialog asked for one.
    const reason = needsReason ? { changeReasonId } : {};
    if (needsReassignment) {
      // Atomic: the server moves every report and deactivates the manager in one
      // transaction, so a failure can't leave reports half-moved.
      deactivate.mutate(
        {
          ...when,
          ...cancels,
          ...reason,
          updates,
          reassignments: reports.map((r) => ({
            personId: r.id,
            managerId: reassignments[r.id] ?? null,
          })),
        },
        {
          onSuccess: () => {
            onScheduled?.();
            onOpenChange(false);
          },
        },
      );
      return;
    }
    update.mutate(
      { ...when, ...cancels, ...reason, updates },
      {
        onSuccess: () => {
          onScheduled?.();
          onOpenChange(false);
        },
      },
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{isImmediate ? "Save changes" : "Schedule changes"}</DialogTitle>
          <DialogDescription>
            Review what's changing.{" "}
            {isImmediate
              ? "The changes take effect immediately when you save."
              : "The record updates on the effective date, not immediately."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <p className="mb-2 text-sm font-medium">Changed fields</p>
            {preview.isLoading ? (
              <div className="space-y-2">
                <Skeleton className="h-5 w-full" />
                <Skeleton className="h-5 w-2/3" />
              </div>
            ) : preview.isError ? (
              <p className="text-sm text-destructive">Couldn't load the changes. Please retry.</p>
            ) : !hasChanges ? (
              <p className="text-sm text-muted-foreground">No changes to save.</p>
            ) : (
              <ul className="space-y-1 rounded-md border bg-muted/40 p-3 text-sm">
                {changes.map((change) => (
                  <ChangeRow key={change.field} change={change} />
                ))}
              </ul>
            )}
          </div>

          {needsReason && (
            <div className="space-y-1.5">
              <Label htmlFor="change-reason">Reason for change</Label>
              <Select
                value={changeReasonId ?? ""}
                onValueChange={(v) => setChangeReasonId(v || null)}
              >
                <SelectTrigger id="change-reason" className="w-full" aria-invalid={missingReason}>
                  <SelectValue
                    placeholder={
                      changeReasons.isLoading ? "Loading reasons…" : "Why is this changing?"
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {(changeReasons.data ?? []).map((reason) => (
                    <SelectItem key={reason.id} value={reason.id}>
                      {reason.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {/* A failed load used to render an empty dropdown behind a
                  REQUIRED field: Save stayed disabled for ever with nothing
                  said. Say it, and offer the retry. */}
              {reasonsUnavailable ? (
                <p className="flex items-center gap-2 text-xs text-danger-foreground">
                  Reasons couldn't be loaded, so this change can't be saved yet.
                  <Button
                    type="button"
                    variant="link"
                    className="h-auto p-0 text-xs"
                    onClick={() => changeReasons.refetch()}
                  >
                    Try again
                  </Button>
                </p>
              ) : (
                <p className="text-xs text-muted-foreground">
                  Required for changes to title, role, classification, department, or manager.
                </p>
              )}
            </div>
          )}

          {vacatesLead && (
            <div className="flex gap-2 rounded-md border border-warning/50 bg-warning-bg p-3 text-sm text-warning-foreground">
              <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" />
              <p>
                This also removes them as <span className="font-medium">Team Lead</span> of{" "}
                <span className="font-medium">{ledTeamName}</span>. The team will have no lead until
                someone assigns a new one on the Organization page.
              </p>
            </div>
          )}

          {reportsUnavailable && (
            <div className="space-y-3 rounded-md border border-warning/50 bg-warning-bg p-3">
              {directReports.isError ? (
                <ErrorState
                  className="py-4"
                  message="Couldn't check who reports to this person. Deactivating now could leave their reports without a manager, so this is blocked until the check succeeds."
                  onRetry={() => void directReports.refetch()}
                />
              ) : (
                <div className="flex gap-2 text-sm text-warning-foreground">
                  <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" />
                  <p>Checking who reports to this person…</p>
                </div>
              )}
            </div>
          )}

          {needsReassignment && (
            <div className="space-y-3 rounded-md border border-warning/50 bg-warning-bg p-3">
              <div className="flex gap-2 text-sm text-warning-foreground">
                <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" />
                <div>
                  <p className="font-medium">
                    {reports.length === 1
                      ? "1 person reports to this manager"
                      : `${reports.length} people report to this manager`}
                  </p>
                  <p className="text-warning-foreground/90">
                    Choose where each direct report moves before deactivating. The moves take effect
                    on the same date as this change.
                  </p>
                </div>
              </div>
              {reports.length > 1 && (
                <div className="space-y-1.5">
                  <Label>Move all to</Label>
                  <EntityCombobox
                    entity="persons"
                    value={null}
                    onChange={(managerId) =>
                      setReassignments(
                        // Skip a report being chosen as their own manager — that
                        // row stays unset for a manual pick.
                        Object.fromEntries(
                          reports.filter((r) => r.id !== managerId).map((r) => [r.id, managerId]),
                        ),
                      )
                    }
                    filters={{ status: "active" }}
                    excludeIds={[personId]}
                    includeNone
                    placeholder="Pick one manager for everyone…"
                    searchPlaceholder="Search people…"
                  />
                </div>
              )}
              <ul className="space-y-2">
                {reports.map((report) => (
                  <li key={report.id} className="grid grid-cols-2 items-center gap-2">
                    <div className="min-w-0 text-sm">
                      <p className="truncate font-medium">{report.name}</p>
                      {report.title && (
                        <p className="truncate text-xs text-muted-foreground">{report.title}</p>
                      )}
                    </div>
                    <EntityCombobox
                      entity="persons"
                      value={reassignments[report.id] ?? null}
                      onChange={(managerId) =>
                        setReassignments((prev) => ({ ...prev, [report.id]: managerId }))
                      }
                      filters={{ status: "active" }}
                      excludeIds={[personId, report.id]}
                      includeNone
                      placeholder="New manager…"
                      aria-invalid={!(report.id in reassignments)}
                      searchPlaceholder="Search people…"
                    />
                  </li>
                ))}
              </ul>
            </div>
          )}

          {autoReplaced.length > 0 && (
            <div className="flex gap-2 rounded-md border border-warning/50 bg-warning-bg p-3 text-sm text-warning-foreground">
              <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" />
              <div>
                <p className="font-medium">Replacing scheduled changes</p>
                <p className="text-warning-foreground/90">
                  {autoReplaced.map((c) => c.label).join(", ")}{" "}
                  {autoReplaced.length === 1 ? "already has" : "already have"}{" "}
                  {isImmediate ? (
                    <>
                      a change scheduled to take effect. Saving now applies immediately and replaces{" "}
                      {autoReplaced.length === 1 ? "it" : "them"}.
                    </>
                  ) : (
                    <>
                      a change scheduled for this same date. Saving replaces{" "}
                      {autoReplaced.length === 1 ? "it" : "them"}.
                    </>
                  )}
                </p>
              </div>
            </div>
          )}

          {overlapChoices.length > 0 && !missingDate && (
            <div className="space-y-2 rounded-md border border-warning/50 bg-warning-bg p-3 text-sm">
              <div className="flex gap-2 text-warning-foreground">
                <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" />
                <div>
                  <p className="font-medium">
                    {overlapChoices.length === 1
                      ? "A change is already scheduled"
                      : "Changes are already scheduled"}
                  </p>
                  <ul className="text-warning-foreground/90">
                    {overlapChoices.map((p) => (
                      <li key={p.id}>
                        {p.label} → {p.newValueLabel} on {formatDateOnly(p.effectiveDate)}
                      </li>
                    ))}
                  </ul>
                  <p className="mt-1 text-warning-foreground/90">
                    Should this change overwrite{" "}
                    {overlapChoices.length === 1
                      ? "that scheduled change"
                      : "those scheduled changes"}
                    , or apply in addition?
                  </p>
                </div>
              </div>
              <div className="flex flex-col gap-1.5 pl-6">
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="overlap-choice"
                    checked={overwrite === "overwrite"}
                    onChange={() => setOverwriteChoice("overwrite")}
                  />
                  Overwrite — cancel the scheduled{" "}
                  {overlapChoices.length === 1 ? "change" : "changes"}
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="overlap-choice"
                    checked={overwrite === "keep"}
                    onChange={() => setOverwriteChoice("keep")}
                  />
                  In addition — each change applies on its own date
                </label>
                {overwrite === "keep" && defaultOverwrite && (
                  <p className="text-xs text-muted-foreground">
                    Heads up: the later scheduled change will replace this value when its date
                    arrives.
                  </p>
                )}
              </div>
            </div>
          )}

          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Checkbox
                id="schedule-for-date"
                checked={scheduleForDate}
                onCheckedChange={(checked) => {
                  setScheduleForDate(checked === true);
                  if (checked !== true) setEffectiveDate("");
                }}
              />
              <Label htmlFor="schedule-for-date" className="font-normal">
                Select a date for this change
              </Label>
            </div>
            {scheduleForDate ? (
              <div className="space-y-1.5">
                <Label htmlFor="effective-date">Effective date</Label>
                <Input
                  id="effective-date"
                  type="date"
                  value={effectiveDate}
                  onChange={(e) => setEffectiveDate(e.target.value)}
                  aria-invalid={!effectiveDate}
                />
                <p className="text-xs text-muted-foreground">
                  When these changes take effect. Today or a past date applies immediately; a future
                  date is scheduled.
                </p>
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">
                The changes take effect immediately when you save.
              </p>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSave}
            disabled={
              missingDate ||
              !hasChanges ||
              reassignmentIncomplete ||
              reportsUnavailable ||
              !pendingSettled ||
              reasonsUnavailable ||
              missingReason ||
              update.isPending ||
              deactivate.isPending
            }
          >
            {update.isPending || deactivate.isPending
              ? isImmediate
                ? "Saving…"
                : "Scheduling…"
              : isImmediate
                ? "Save changes"
                : "Schedule change"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ChangeRow({ change }: { change: PersonChangePreview }) {
  return (
    <li className="text-muted-foreground">
      <span className="font-medium text-foreground">{change.label}</span>
      {": "}
      <span>{change.beforeLabel}</span>
      <ArrowRight className="mx-1 inline h-3 w-3" />
      <span className="text-foreground">{change.afterLabel}</span>
    </li>
  );
}
